/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { action, computed, makeObservable, observable } from 'mobx';
import { Student } from '../../types/Student';
import { Message } from '../../types/Message';

export const publicChatKey = 'public';

export class ChatModel {
	constructor() {
		makeObservable(this);
	}

	@observable
	public activeChat: string = publicChatKey;

	@observable
	public students: Student[] = [];

	@observable
	public areStudentsBeingFetched = true;

	@observable
	public messageIdentifiers: string[] = [];

	@observable
	public messages: { [key: string]: Message[] } = {};

	@observable
	public unreadMessageIdentifiers: string[] = [];

	@observable
	public unreadMessagesAmount: { [key: string]: number } = {};

	@observable
	public areMessagesBeingFetched: { [key: string]: boolean } = {};

	@observable
	public message = '';

	@observable
	public messageToDelete = '';

	@observable
	public reload = false;

	@computed
	get messagesLoaded(): boolean {
		if (this.areMessagesBeingFetched[this.activeChat] === undefined) {
			return true;
		}

		return this.areMessagesBeingFetched[this.activeChat];
	}

	@action
	setActiveChat(key: string): void {
		this.activeChat = key;
	}

	@action
	setStudents(students: Student[]): void {
		this.students = students;
	}

	@action
	setAreStudentsBeingFetched(bool: boolean): void {
		this.areStudentsBeingFetched = bool;
	}

	@action
	setMessages(key: string, messages: Message[]): void {
		this.messages[key] = messages;
	}

	@action
	addMessageIdentifier(identifier: string): void {
		this.messageIdentifiers.push(identifier);
	}

	@action
	addUnreadMessage(identifier: string, key: string): void {
		if (!!this.unreadMessagesAmount[key]) {
			this.unreadMessagesAmount[key] += 1;
		} else {
			this.unreadMessagesAmount[key] = 1;
		}
		this.unreadMessageIdentifiers.push(identifier);
	}

	@action
	resetUnreadMessages(key: string): void {
		this.unreadMessagesAmount[key] = 0;
	}

	@action
	setAreMessagesBeingFetched(key: string, bool: boolean): void {
		this.areMessagesBeingFetched[key] = bool;
	}

	@action
	setMessage(message: string): void {
		this.message = message;
	}

	@action
	setMessageToDelete(identifier: string): void {
		this.messageToDelete = identifier;
	}
}
